import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, Check, Download, AlertTriangle, ArrowLeft } from "lucide-react";
import { api, API } from "../lib/api";
import Seo from "../components/Seo";

const MAX_ATTEMPTS = 6;
const POLL_INTERVAL = 2000;

export default function CheckoutSuccessPage() {
  const [state, setState] = useState("checking"); // checking | success | timeout | error
  const [info, setInfo] = useState(null);

  useEffect(() => {
    let cancelled = false;

    const poll = async (attempt) => {
      if (cancelled) return;
      if (attempt >= MAX_ATTEMPTS) {
        setState("timeout");
        return;
      }
      try {
        // Cookie-based status. The Stripe session id is never in the URL or this request.
        const { data } = await api.get(`/checkout/session-status`, { withCredentials: true });
        if (cancelled) return;
        setInfo(data);
        if (data.payment_status === "paid") {
          setState("success");
          return;
        }
        setTimeout(() => poll(attempt + 1), POLL_INTERVAL);
      } catch (err) {
        if (cancelled) return;
        // 404 (no cookie) or 410 (expired) => no recoverable session on this device
        const status = err?.response?.status;
        if (status === 404 || status === 410) {
          setState("error");
          return;
        }
        setTimeout(() => poll(attempt + 1), POLL_INTERVAL);
      }
    };
    poll(0);
    return () => { cancelled = true; };
  }, []);

  return (
    <div data-testid="checkout-success-page" className="mx-auto max-w-2xl px-5 py-32 min-h-[60vh]">
      <Seo title="Order Confirmation" description="Your purchase confirmation." path="/checkout/success" robots="noindex, nofollow" />

      {state === "checking" && (
        <div data-testid="checkout-checking" className="text-center">
          <Loader2 className="mx-auto h-10 w-10 text-primary animate-spin" />
          <h1 className="mt-6 font-display text-3xl">Confirming your payment</h1>
          <p className="mt-3 text-sm text-foreground/60">This usually takes a few seconds. Please don't close this tab.</p>
        </div>
      )}

      {state === "success" && (
        <div data-testid="checkout-success" className="border border-primary/40 bg-primary/5 p-10 text-center">
          <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-primary/15 border border-primary/40">
            <Check className="h-8 w-8 text-primary" />
          </div>
          <p className="mono-label mt-6 text-primary">Payment Confirmed</p>
          <h1 className="mt-3 font-display text-3xl tracking-tight">Thank you for your purchase</h1>
          <p className="mt-4 text-sm text-foreground/65 leading-relaxed">
            You now have access to <span className="text-foreground font-medium">{info?.product_name}</span>. Your purchase includes <span className="text-foreground font-medium">two PDF versions</span> — download both below. A receipt has been sent by Stripe to your email.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-3">
            <a
              data-testid="download-designed-btn"
              href={`${API}/checkout/download?variant=designed`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 bg-primary text-primary-foreground font-medium text-sm px-7 py-3.5 hover:bg-primary-hover transition-colors shadow-[0_0_20px_rgba(0,229,153,0.3)]"
            >
              <Download className="h-4 w-4" /> Designed Brand PDF
            </a>
            <a
              data-testid="download-plain-btn"
              href={`${API}/checkout/download?variant=plain`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 border border-primary text-primary font-medium text-sm px-7 py-3.5 hover:bg-primary hover:text-primary-foreground transition-colors"
            >
              <Download className="h-4 w-4" /> Plain Text PDF
            </a>
          </div>
          <p className="mt-4 text-xs text-muted-foreground">
            The <span className="text-foreground/70">Designed Brand</span> version is best for reading on screen; the <span className="text-foreground/70">Plain Text</span> version is print-friendly and easy to search.
          </p>
          <p className="mt-4 text-xs text-muted-foreground">
            Trouble downloading? Email <a href="mailto:info@myecoengineer.com" className="text-primary hover:underline">info@myecoengineer.com</a> — a permanent download link was also sent to your inbox.
          </p>
        </div>
      )}

      {(state === "timeout" || state === "error") && (
        <div data-testid="checkout-pending" className="border border-border bg-surface p-10 text-center">
          <AlertTriangle className="mx-auto h-10 w-10 text-foreground/50" />
          <h1 className="mt-6 font-display text-2xl">
            {state === "timeout" ? "Still processing" : "We couldn't confirm this payment here"}
          </h1>
          <p className="mt-3 text-sm text-foreground/60">
            {state === "timeout"
              ? "Your payment is taking longer than usual to confirm. If you completed checkout, your access and download link have been emailed to you."
              : "We couldn't find an active confirmation on this device. If you completed checkout, your product and download link have been emailed to you automatically."}
          </p>
          <p className="mt-5 text-xs text-muted-foreground">
            Questions? <a href="mailto:info@myecoengineer.com" className="text-primary hover:underline">info@myecoengineer.com</a>
          </p>
          <Link to="/products" className="mt-7 inline-flex items-center gap-2 text-primary font-mono text-[11px] uppercase tracking-widest">
            <ArrowLeft className="h-4 w-4" /> Back to Products
          </Link>
        </div>
      )}
    </div>
  );
}
